import { ClipboardCheck, History, PlayCircle, RotateCcw, Settings, SquarePen, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { usePermission } from '../../../../auth/AuthContext';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { RecordCountModal } from '../components/RecordCountModal';
import { StartInventoryAuditModal } from '../components/StartInventoryAuditModal';
import { inventoryAuditsApi } from '../inventoryAuditApi';
import type { InventoryAuditDetail, InventoryAuditLineDetail } from '../inventoryAuditTypes';
import { INVENTORY_AUDIT_STATUS, isAuditConcluded } from '../inventoryAuditTypes';

type TabKey = 'info' | 'lines';

/**
 * One physical stock take — its scope, its progress, and the sheet it was counted on.
 *
 * <b>It LANDS on the count sheet rather than on Info</b>, which is the document profile's own
 * exception read for a second record: an auditor opens this to COUNT, where every other profile
 * in this product is opened to check a record's fields. `Info` is still the first tab DECLARED,
 * so the strip's order and the check the shell makes in development are untouched — only where
 * the page lands.
 *
 * <b>No card carries an Edit, and that is the module's shape rather than unfinished work.</b>
 * There is no update endpoint: a raised audit's window and stores are what the baseline was
 * captured against, so the things that can still be done to it are record-level — start it,
 * complete it, cancel it — and those sit in the header's cog where a record-level action belongs.
 */
export function InventoryAuditProfilePage() {
  const { id = '' } = useParams<{ id: string }>();

  const [record, setRecord] = useState<InventoryAuditDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const [tab, setTab] = useState<TabKey>('lines');
  const [showDeleted, setShowDeleted] = useState(false);

  const [counting, setCounting] = useState<InventoryAuditLineDetail | null>(null);
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [auditorName, setAuditorName] = useState<string | null>(null);
  const [accountableNames, setAccountableNames] = useState<Map<string, string>>(new Map());

  const canReadPersonnel = usePermission('personnel.read');

  const load = useCallback(
    async (withDeleted: boolean) => {
      try {
        setRecord(await inventoryAuditsApi.get(id, withDeleted));
        setError(null);
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not open this audit.', 0),
        );
      }
    },
    [id],
  );

  useEffect(() => {
    // A different record drops what is on screen first, so the reader gets the first-load
    // treatment rather than the previous audit sitting under the new one's breadcrumb.
    setRecord(null);
    void load(showDeleted);
  }, [load, showDeleted]);

  /*
    The auditor and every accountable employee resolved in ONE batch for the page on screen —
    personnel by id, then a single `persons/resolve`. Two calls rather than one per person, which
    is the cap that exists to stop a leaked token walking the identity store.
  */
  useEffect(() => {
    if (record == null || !canReadPersonnel) return;

    const ids = [
      ...(record.auditedByPersonnelId == null ? [] : [record.auditedByPersonnelId]),
      ...record.lines.map((l) => l.accountablePersonnelId).filter((x): x is string => x != null),
    ];

    const distinct = [...new Set(ids)];
    if (distinct.length === 0) return;

    const controller = new AbortController();

    void (async () => {
      try {
        const records = await Promise.all(
          distinct.map((personnelId) => personnelApi.get(personnelId, controller.signal)),
        );

        const resolved = await personsApi.resolve(
          records.map((p) => p.personId),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPerson = new Map(resolved.map((p) => [p.personId, p.displayName]));
        const byPersonnel = new Map(
          records.map((p) => [p.id, byPerson.get(p.personId) ?? p.employeeNumber]),
        );

        setAccountableNames(byPersonnel);
        setAuditorName(
          record.auditedByPersonnelId == null
            ? null
            : byPersonnel.get(record.auditedByPersonnelId) ?? null,
        );
      } catch {
        // Fails SOFT: a failed resolve leaves an em dash rather than blanking the page.
      }
    })();

    return () => controller.abort();
  }, [record, canReadPersonnel]);

  const lines = useMemo(() => record?.lines ?? [], [record]);

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      /*
        Searched, sorted and paged IN MEMORY. The rule against client-side filtering is about a
        PAGED list whose filter silently applies to the rows on screen and not the hundreds
        behind them — a count sheet arrives WHOLE with the record, so the filter sees everything
        there is. The accountable employee is deliberately NOT searchable: the name lives in
        another database and is resolved for the page, so matching it would search what happens
        to have loaded.
      */
      const matched = term
        ? lines.filter((l) =>
            [l.itemName, l.itemSku, l.inventoryCategoryName ?? '', l.varianceReason ?? ''].some(
              (value) => value.toLowerCase().includes(term),
            ),
          )
        : lines;

      const field = query.sort?.replace(/^-/, '') ?? 'itemName';
      const direction = query.sort?.startsWith('-') ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (
          field === 'snapshotQuantity' ||
          field === 'actualQuantity' ||
          field === 'varianceQuantity' ||
          field === 'adjustmentQuantity'
        ) {
          const av = a[field] as number | null;
          const bv = b[field] as number | null;

          // An UNCOUNTED row sorts LAST whichever way the column points — "nobody has looked" is
          // not a small number, and burying it among the counts would hide what is left to do.
          if (av == null && bv == null) return a.itemName.localeCompare(b.itemName);
          if (av == null) return 1;
          if (bv == null) return -1;

          const gap = (av - bv) * direction;
          return gap !== 0 ? gap : a.itemName.localeCompare(b.itemName);
        }

        const pick = (row: InventoryAuditLineDetail) =>
          field === 'itemSku'
            ? row.itemSku
            : field === 'inventoryCategoryName'
              ? (row.inventoryCategoryName ?? '')
              : row.itemName;

        return pick(a).localeCompare(pick(b)) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [lines],
  );

  const state = useDataTable<InventoryAuditLineDetail>({
    fetchPage,
    pageSize: 25,
    defaultSort: 'itemName',
  });

  async function run(action: () => Promise<InventoryAuditDetail>) {
    setRunning(true);
    setError(null);

    try {
      setRecord(await action());
      setStarting(false);
      setCompleting(false);
      setCancelling(false);
      setCancelReason('');
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  async function restore() {
    setRestoring(true);

    try {
      await inventoryAuditsApi.activate(id);
      await load(showDeleted);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Inventory Audits', to: '/inventory/inventory-audits' },
    record?.auditNumber ?? 'Audit',
  ];

  if (record == null) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-1 items-center justify-center p-8">
          {/*
            Guarded on having NOTHING to show, never on `error` alone: once a record is up it is
            still the last good answer, and a failed refresh belongs in the banner rather than
            replacing the page.
          */}
          <p className="text-sm text-slate-500">{error ? error.message : 'Loading the audit…'}</p>
        </div>
      </div>
    );
  }

  const isDraft = record.status === INVENTORY_AUDIT_STATUS.draft;
  const isCounting = record.status === INVENTORY_AUDIT_STATUS.inProgress;
  const concluded = isAuditConcluded(record.status);

  const actions: MenuItem[] = [
    ...(isDraft
      ? [
          {
            key: 'start',
            label: 'Start count',
            icon: <PlayCircle className="h-4 w-4" aria-hidden="true" />,
            permission: 'inventory-audits.start',
            onClick: () => setStarting(true),
          } satisfies MenuItem,
        ]
      : []),
    ...(isCounting
      ? [
          {
            key: 'complete',
            label: 'Complete audit',
            permission: 'inventory-audits.complete',
            disabledReason:
              record.countedItems === 0
                ? 'Record at least one physical count before completing.'
                : null,
            onClick: () => setCompleting(true),
          } satisfies MenuItem,
        ]
      : []),
    ...(concluded
      ? []
      : [
          {
            key: 'cancel',
            label: 'Cancel audit',
            icon: <XCircle className="h-4 w-4" aria-hidden="true" />,
            permission: 'inventory-audits.complete',
            onClick: () => {
              setCancelReason('');
              setCancelling(true);
            },
          } satisfies MenuItem,
        ]),
    {
      key: 'history',
      label: 'History logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setHistoryOpen(true),
    },
  ];

  /*
    A banner for EVERY state, not only the ones that are wrong — a strip that appears only when
    something needs attention is one the reader learns to skip, and then it is missed on the day
    it matters.
  */
  const banner = !record.isActive
    ? { tone: 'slate' as const, message: 'This audit is deleted. Restoring puts it back in the register.' }
    : isDraft
      ? {
          tone: 'blue' as const,
          message:
            'Raised, and nothing is counted yet. Starting captures what the system believes is on hand at the audited stores and opens the count sheet.',
        }
      : isCounting
        ? {
            tone: 'blue' as const,
            message: `Counting — ${record.countedItems} of ${record.totalItems} rows recorded. No stock moves: completing documents the reconciliation, and correcting the shelf is a stock adjustment raised separately.`,
          }
        : record.status === INVENTORY_AUDIT_STATUS.completed
          ? {
              tone: 'slate' as const,
              message: `Completed${record.completedAt == null ? '' : ` on ${formatDateTime(record.completedAt)}`}. ${record.itemsWithVariance} row${record.itemsWithVariance === 1 ? '' : 's'} had a variance, net ${signed(record.netVariance)}. Record-only — no stock was changed.`,
            }
          : {
              tone: 'slate' as const,
              message:
                record.cancellationReason == null
                  ? 'This audit was cancelled.'
                  : `Cancelled: ${record.cancellationReason}`,
            };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="inventory-audit-profile"
        header={
          <ProfileHeader
            id="inventory-audit-profile-header"
            icon={<ClipboardCheck className="h-6 w-6" aria-hidden="true" />}
            title={record.auditNumber}
            meta={[
              `${formatDateOnly(record.auditStartDate)} – ${formatDateOnly(record.auditEndDate)}`,
              record.facilities.map((f) => f.storageFacilityName).join(', ') || null,
              canReadPersonnel ? (auditorName == null ? null : `Counted by ${auditorName}`) : null,
            ]}
            secondary={[
              `${record.countedItems} of ${record.totalItems} counted`,
              record.countedItems === 0 ? null : `${record.itemsWithVariance} flagged`,
              record.countedItems === 0 ? null : `net ${signed(record.netVariance)}`,
              record.snapshotAsOf == null
                ? 'Baseline not captured yet'
                : `Inventory as of ${formatDateTime(record.snapshotAsOf)}`,
            ]}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>
                {record.isActive ? record.statusLabel : 'Deleted'}
              </StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="inventory-audit-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Audit settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <ProfileBanner
            id="inventory-audit-profile-banner"
            tone={banner.tone}
            message={banner.message}
            action={
              !record.isActive ? (
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="inventory-audits.delete">
                  <Button
                    id="inventory-audit-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Line items <CountBadge count={record.totalItems} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        panel={tab === 'lines' ? 'bleed' : 'cards'}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard id="inventory-audit-details" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Audit number', value: record.auditNumber, mono: true },
                  { label: 'Window opens', value: formatDateOnly(record.auditStartDate), mono: true },
                  { label: 'Window closes', value: formatDateOnly(record.auditEndDate), mono: true },
                  { label: 'Stage', value: record.statusLabel },
                  {
                    label: 'Counted by',
                    value: canReadPersonnel
                      ? auditorName
                      : record.auditedByPersonnelId == null
                        ? null
                        : 'Hidden',
                  },
                  {
                    label: 'Stores counted',
                    value: record.facilities.map((f) => f.storageFacilityName).join(', ') || null,
                  },
                  {
                    /*
                      Null until Start, and it SAYS which rather than showing an em dash — "not
                      captured yet" and "no value" are different facts, and only one of them tells
                      the reader what to press.
                    */
                    label: 'Inventory as of',
                    value:
                      record.snapshotAsOf == null
                        ? 'Not captured yet — taken when the count starts'
                        : formatDateTime(record.snapshotAsOf),
                    mono: record.snapshotAsOf != null,
                  },
                  {
                    label: 'Completed',
                    value: record.completedAt == null ? null : formatDateTime(record.completedAt),
                    mono: true,
                  },
                  { label: 'Notes', value: record.notes },
                  { label: 'Cancellation', value: record.cancellationReason },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="inventory-audit-progress" title="Progress">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Rows on the sheet', value: record.totalItems, mono: true },
                  { label: 'Counted', value: record.countedItems, mono: true },
                  {
                    label: 'With a variance',
                    value: record.countedItems === 0 ? null : record.itemsWithVariance,
                    mono: true,
                  },
                  {
                    label: 'Net variance',
                    value: record.countedItems === 0 ? null : signed(record.netVariance),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="inventory-audit-trail" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  { label: 'Created at', value: formatDateTime(record.createdAt), mono: true },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated at',
                    value: record.updatedAt == null ? null : formatDateTime(record.updatedAt),
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          <DataTablePanel<InventoryAuditLineDetail>
            id="inventory-audit-lines"
            state={state}
            columns={LINE_COLUMNS(accountableNames, canReadPersonnel)}
            rowKey={(line) => line.id}
            searchPlaceholder="Search item, SKU, category or reason"
            emptyMessage={
              isDraft
                ? 'The count sheet is generated when the audit starts.'
                : 'Nothing is on this count sheet.'
            }
            emptySearchMessage="No row on this sheet matches that search."
            minWidth={1180}
            fill
            /*
              A panel offering Show deleted owes a status column, or a struck row arrives wearing
              no marker at all — the one state a row must never be silent about. The column is
              below, carrying `status: true`.
            */
            settingsItems={[
              {
                key: 'show-deleted',
                label: 'Show deleted',
                checked: showDeleted,
                onClick: () => setShowDeleted((v) => !v),
              },
            ]}
            rowActions={(line) =>
              isCounting
                ? [
                    {
                      key: `count-${line.id}`,
                      label: 'Record count',
                      icon: <SquarePen className="h-4 w-4" aria-hidden="true" />,
                      permission: 'inventory-audits.record-count',
                      disabledReason: line.isActive ? null : 'This row is deleted.',
                      onClick: () => setCounting(line),
                    },
                    {
                      key: `restore-${line.id}`,
                      label: 'Restore row',
                      permission: 'inventory-audits.record-count',
                      disabledReason: line.isActive ? 'This row is already active.' : null,
                      onClick: () => {
                        void (async () => {
                          await inventoryAuditsApi.restoreLine(id, line.id);
                          await load(showDeleted);
                        })();
                      },
                    },
                  ]
                : []
            }
            onDelete={
              isCounting
                ? (line) => {
                    void (async () => {
                      await inventoryAuditsApi.deleteLine(id, line.id);
                      await load(showDeleted);
                    })();
                  }
                : undefined
            }
            deletePermission="inventory-audits.record-count"
            deleteLabel={() => 'Delete row'}
            deleteDisabledReason={(line) => (line.isActive ? null : 'This row is already deleted.')}
            selectable={false}
            /*
              NULL, because a count-sheet row is generated rather than authored: it has no creator
              a reader would recognise and no trail of its own worth three columns of em dashes.
              `auditColumns={false}` goes with it in the same breath.
            */
            otherInfo={() => null}
            auditColumns={false}
            rowPreview={{ title: 'Count sheet row', size: 'lg' }}
          />
        )}
      </ProfileHeaderShell>

      {counting ? (
        <RecordCountModal
          open
          auditId={id}
          line={counting}
          onClose={() => setCounting(null)}
          onSaved={(saved) => {
            setCounting(null);
            setRecord(saved);
          }}
        />
      ) : null}

      {starting ? (
        <StartInventoryAuditModal
          open
          auditId={id}
          auditNumber={record.auditNumber}
          auditStartDate={record.auditStartDate}
          facilities={record.facilities}
          onClose={() => setStarting(false)}
          onStarted={(saved) => {
            setStarting(false);
            setRecord(saved);
          }}
        />
      ) : null}

      {completing ? (
        <ConfirmDialog
          open
          onClose={() => setCompleting(false)}
          onConfirm={() => void run(() => inventoryAuditsApi.complete(id))}
          title="Complete inventory audit"
          requireConfirm
          loading={running}
          id="inventory-audit-complete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {record.auditNumber} becomes read-only. Its variances are kept as the
                reconciliation record.
              </p>
              <p>
                <b>No stock moves.</b> Correcting a shelf to match a count is a stock adjustment,
                raised separately under its own key and with its own reason.
              </p>
              <p>
                {record.countedItems} of {record.totalItems} rows counted, {record.itemsWithVariance}{' '}
                flagged, net {signed(record.netVariance)}.
              </p>
            </div>
          }
        />
      ) : null}

      {cancelling ? (
        <Modal
          open
          onClose={() => setCancelling(false)}
          title="Cancel inventory audit"
          size="md"
          id="inventory-audit-cancel-modal"
          footer={
            <>
              <Button
                id="inventory-audit-cancel-back"
                variant="secondary"
                onClick={() => setCancelling(false)}
                disabled={running}
              >
                Keep it
              </Button>
              <Button
                id="inventory-audit-cancel-submit"
                variant="danger"
                onClick={() =>
                  void run(() =>
                    inventoryAuditsApi.cancel(id, { reason: cancelReason.trim() }),
                  )
                }
                disabled={cancelReason.trim() === '' || running}
                loading={running}
                loadingLabel="Cancelling"
              >
                Cancel audit
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">
              <b>Cancelling is not deleting.</b> It records that the count was abandoned and why —
              which stays on the register. Deleting hides a sheet raised against the wrong window.
            </p>

            <Textarea
              id="inventory-audit-cancel-reason"
              label="Reason"
              description="Why this count is being abandoned. It stays on the record."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              minRows={3} maxRows={8}
              maxLength={500}
              required
              disabled={running}
              error={cancelReason.trim() === '' ? 'Say why this audit is being cancelled.' : undefined}
            />
          </div>
        </Modal>
      ) : null}

      {historyOpen ? (
        <HistoryLogsModal
          open
          onClose={() => setHistoryOpen(false)}
          module="inventory-audits"
          entityId={id}
        />
      ) : null}
    </div>
  );
}

/** A variance reads with its sign, so a surplus and a shortfall are told apart at a glance. */
function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

/**
 * The source's own eight columns, in its own order.
 *
 * ACCOUNTABLE resolves through the identity store for the page on screen, and reads `Hidden`
 * without `personnel.read` — the record HAS somebody answerable, this page has not been given
 * the name, and an em dash there would state that nobody is accountable.
 */
const LINE_COLUMNS = (
  names: Map<string, string>,
  canReadPersonnel: boolean,
): DataColumn<InventoryAuditLineDetail>[] => [
  {
    key: 'itemName',
    header: 'Item',
    width: 260,
    sortable: true,
    render: (line) => <TextCell value={line.itemName} />,
  },
  {
    key: 'itemSku',
    header: 'SKU',
    width: 160,
    sortable: true,
    render: (line) => <TextCell value={line.itemSku} mono />,
  },
  {
    key: 'inventoryCategoryName',
    header: 'Category',
    width: 170,
    sortable: true,
    render: (line) => <TextCell value={line.inventoryCategoryName} />,
  },
  {
    key: 'snapshotQuantity',
    header: 'Current',
    width: 110,
    sortable: true,
    render: (line) => (
      <TextCell value={`${line.snapshotQuantity} ${line.unitOfMeasureName}`} mono numeric />
    ),
  },
  {
    key: 'actualQuantity',
    header: 'Actual',
    width: 100,
    sortable: true,
    // Null is "nobody has counted this", which is not a zero — the em dash is the honest answer.
    render: (line) => (
      <TextCell value={line.actualQuantity == null ? null : String(line.actualQuantity)} mono numeric />
    ),
  },
  {
    key: 'varianceQuantity',
    header: 'Variance',
    width: 110,
    sortable: true,
    render: (line) => (
      <TextCell
        value={line.varianceQuantity == null ? null : signed(line.varianceQuantity)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'adjustmentQuantity',
    header: 'Adjustment',
    width: 120,
    sortable: true,
    render: (line) => (
      <TextCell
        value={line.adjustmentQuantity == null ? null : signed(line.adjustmentQuantity)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'varianceReason',
    header: 'Reason',
    width: 220,
    render: (line) => <TextCell value={line.varianceReason} />,
  },
  {
    key: 'accountable',
    header: 'Accountable',
    width: 190,
    render: (line) => (
      <TextCell
        value={
          line.accountablePersonnelId == null
            ? null
            : canReadPersonnel
              ? (names.get(line.accountablePersonnelId) ?? null)
              : 'Hidden'
        }
      />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (line) => (
      <StatusText tone={line.isActive ? 'active' : 'inactive'}>
        {line.isActive ? 'Active' : 'Deleted'}
      </StatusText>
    ),
  },
];
